import { useRef, type Ref } from "react";
import { useForkRef } from "@mui/material/utils";

import { fieldDescribedBy } from "ui/forms/field-message/field-message.helpers";
import { useFieldId } from "ui/forms/use-field-id";
import { type TextFieldProps } from "ui/text-field/text-field.types";

type UseTextFieldProps = Pick<
  TextFieldProps,
  | "id"
  | "name"
  | "ref"
  | "value"
  | "disabled"
  | "readOnly"
  | "errMessage"
  | "helperText"
  | "endIcon"
  | "inputActions"
  | "onClear"
  | "data-testid"
>;

export const useTextField = ({
  id,
  name,
  ref,
  value,
  disabled,
  readOnly,
  errMessage,
  helperText,
  endIcon,
  inputActions,
  onClear,
  "data-testid": dataTestId,
}: UseTextFieldProps) => {
  // The label is rendered by `InputControl` as a `FormLabel htmlFor={id}`, so the control needs a
  // matching `id` or the label names nothing — clicking it does not focus the input and assistive tech
  // never reads it. `name` is not an `id`.
  const fieldId = useFieldId(id, name);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  // The prop is a union of the two element refs a field can render; `useForkRef` takes one type.
  const forkedRef = useForkRef(ref as Ref<HTMLInputElement | HTMLTextAreaElement>, inputRef);

  const showClear = !!onClear && !!value && !disabled && !readOnly;
  const hasInputActions = !!inputActions;

  // The button unmounts with the value it cleared, so focus goes back to the input rather than to
  // the page.
  const handleClear = () => {
    onClear?.();
    inputRef.current?.focus();
  };

  return {
    fieldId,
    inputRef: forkedRef,
    showClear,
    hasInputActions,
    hasEndContent: hasInputActions || showClear || !!endIcon,
    handleClear,
    describedBy: fieldDescribedBy(fieldId, { errorMessage: errMessage, helperText }),
    testId: dataTestId ?? `${name ?? fieldId}-text-input`,
  };
};
